import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';

const PaymentLogs = () => {
  const location = useLocation();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'ALL',
    paymentMode: 'ALL',
    feeCategory: 'ALL',
    dateFrom: '',
    dateTo: '',
    searchQuery: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalAmount: 0
  });

  useEffect(() => {
    fetchPaymentLogs();
    // Check if USN is in URL params
    const params = new URLSearchParams(location.search);
    const usn = params.get('usn');
    if (usn && usn !== 'null') {
      setFilters(prev => ({ ...prev, searchQuery: usn }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchPaymentLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/fees/all-payments');
      setPayments(response.data.data);
      calculateStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch payment logs:', error);
      toast.error('Failed to load payment logs');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const stats = {
      total: data.length,
      pending: data.filter(p => p.verificationStatus === 'PENDING').length,
      approved: data.filter(p => p.verificationStatus === 'VERIFIED').length,
      rejected: data.filter(p => p.verificationStatus === 'REJECTED').length,
      totalAmount: data
        .filter(p => p.verificationStatus === 'VERIFIED')
        .reduce((sum, p) => sum + parseFloat(p.amountPaid || 0), 0)
    };
    setStats(stats);
  };

  const filteredPayments = payments.filter(payment => {
    // Status filter
    if (filters.status !== 'ALL' && payment.verificationStatus !== filters.status) {
      return false;
    }

    // Payment mode filter
    if (filters.paymentMode !== 'ALL' && payment.paymentMode !== filters.paymentMode) {
      return false;
    }

    // Fee category filter
    if (filters.feeCategory !== 'ALL' && payment.feeCategory !== filters.feeCategory) {
      return false;
    }

    // Date range filter
    if (filters.dateFrom && new Date(payment.createdAt) < new Date(filters.dateFrom)) {
      return false;
    }
    if (filters.dateTo && new Date(payment.createdAt) > new Date(filters.dateTo)) {
      return false;
    }

    // Search query filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      return (
        payment.user?.name?.toLowerCase().includes(query) ||
        payment.user?.usn?.toLowerCase().includes(query) ||
        payment.transactionId?.toLowerCase().includes(query) ||
        payment.receiptNumber?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  const exportToCSV = () => {
    const headers = ['Date', 'Receipt No', 'Student Name', 'USN', 'Department', 'Semester', 'Amount', 'Payment Mode', 'Category', 'Status', 'Transaction ID', 'Verified By', 'Remarks'];
    
    const rows = filteredPayments.map(payment => [
      new Date(payment.createdAt).toLocaleDateString(),
      payment.receiptNumber || '-',
      payment.user?.name || '-',
      payment.user?.usn || '-',
      payment.user?.department || '-',
      payment.user?.semester || '-',
      payment.amountPaid,
      payment.paymentMode,
      payment.feeCategory,
      payment.verificationStatus,
      payment.transactionId || '-',
      payment.verifiedBy?.name || '-',
      payment.verificationRemarks || '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Payment logs exported successfully');
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      VERIFIED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Payment Logs</h1>
              <p className="text-gray-600 text-lg">View all payment transactions and history</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => window.location.href = '/fees/record'}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all shadow-md hover:shadow-lg font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Record
              </button>
              <button
                onClick={() => window.location.href = '/fees/verify'}
                className="flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all shadow-md hover:shadow-lg font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Verify
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-md hover:shadow-lg font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </button>
            </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
          <p className="text-sm font-medium text-gray-600 mb-1">Total Payments</p>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
          <p className="text-sm font-medium text-gray-600 mb-1">Pending</p>
          <p className="text-3xl font-bold text-gray-900">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
          <p className="text-sm font-medium text-gray-600 mb-1">Approved</p>
          <p className="text-3xl font-bold text-gray-900">{stats.approved}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
          <p className="text-sm font-medium text-gray-600 mb-1">Rejected</p>
          <p className="text-3xl font-bold text-gray-900">{stats.rejected}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
          <p className="text-sm font-medium text-gray-600 mb-1">Total Amount</p>
          <p className="text-3xl font-bold text-gray-900">₹{stats.totalAmount.toLocaleString()}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-700">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
            <select
              value={filters.paymentMode}
              onChange={(e) => setFilters({ ...filters, paymentMode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ALL">All Modes</option>
              <option value="ONLINE">Online</option>
              <option value="CASH">Cash</option>
              <option value="UPI">UPI</option>
              <option value="CARD">Card</option>
              <option value="CHEQUE">Cheque</option>
              <option value="DD">DD</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filters.feeCategory}
              onChange={(e) => setFilters({ ...filters, feeCategory: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ALL">All Categories</option>
              <option value="TUITION_FEE">Tuition Fee</option>
              <option value="EXAM_FEE">Exam Fee</option>
              <option value="LIBRARY_FEE">Library Fee</option>
              <option value="HOSTEL_FEE">Hostel Fee</option>
              <option value="TRANSPORT_FEE">Transport Fee</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Name, USN, Transaction ID..."
              value={filters.searchQuery}
              onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <button
          onClick={() => setFilters({
            status: 'ALL',
            paymentMode: 'ALL',
            feeCategory: 'ALL',
            dateFrom: '',
            dateTo: '',
            searchQuery: ''
          })}
          className="mt-4 px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          Clear Filters
        </button>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {filteredPayments.length} of {payments.length} payments
      </div>

      {/* Payment Logs Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Date
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Student Details
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Amount
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Payment Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Additional Info
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No payment records found
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment, index) => (
                  <tr key={payment.id} className="hover:bg-blue-50 transition-colors duration-150">
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {new Date(payment.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 pl-6">
                        {new Date(payment.createdAt).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div>
                        <div className="font-semibold text-gray-900 text-base">{payment.user?.name || 'N/A'}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">{payment.user?.usn || 'N/A'}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {payment.user?.department || ''} {payment.user?.department && payment.user?.semester && '•'} {payment.user?.semester ? `Sem ${payment.user.semester.replace('SEM_', '')}` : ''}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-2xl font-bold text-gray-900">₹{parseFloat(payment.amountPaid).toLocaleString()}</span>
                        <span className="text-xs text-gray-500 mt-1">Indian Rupees</span>
                      </div>
                    </td>
                    <td className="px-6 py-5 w-1">
                      <div className="flex flex-wrap gap-2">
                        {payment.receiptNumber && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
                            {payment.receiptNumber}
                          </span>
                        )}
                        <span className={
                          `inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                            payment.paymentMode === 'UPI' ? 'bg-purple-100 text-purple-800' :
                            payment.paymentMode === 'CASH' ? 'bg-green-100 text-green-800' :
                            payment.paymentMode === 'CARD' ? 'bg-blue-100 text-blue-800' :
                            payment.paymentMode === 'NEFT' ? 'bg-indigo-100 text-indigo-800' :
                            'bg-gray-100 text-gray-800'
                          }`
                        }>
                          {payment.paymentMode}
                        </span>
                        <span className="text-xs font-medium text-gray-700 bg-amber-50 px-2 py-1 rounded border border-amber-200 whitespace-nowrap">
                          {payment.feeCategory.replace(/_/g, ' ')}
                        </span>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getStatusBadge(payment.verificationStatus)}`}>
                          {payment.verificationStatus}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="space-y-2 text-sm">
                        {payment.transactionId && (
                          <div className="text-gray-900">
                            <span className="font-medium text-gray-500">Transaction ID: </span>
                            <span className="font-medium">{payment.transactionId}</span>
                          </div>
                        )}
                        {payment.verifiedBy?.name && (
                          <div className="text-gray-700">
                            <span className="font-medium text-gray-500">Verified By: </span>
                            <span>{payment.verifiedBy.name}</span>
                          </div>
                        )}
                        {payment.verificationRemarks && (
                          <div className="text-gray-700">
                            <span className="font-medium text-gray-500 text-xs">Remarks: </span>
                            <span className="text-gray-500 text-xs max-w-xs" title={payment.verificationRemarks}>
                              {payment.verificationRemarks}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
};

export default PaymentLogs;
